import { PaymentRythm } from '@qover/domain';
import { Draft, DraftProps } from './Draft';
import type { Variant } from './Terms';
import type { Type } from './Risk';
import { messagesPerVariant } from '../bike.messages';
import { PriceInfo } from './PriceInfo';

const formatPrice = (price: number) => (price / 100).toFixed(2);

const createVariantView = (
  prices: PriceInfo,
  variant: Variant,
  type: Type,
  country: string,
  discountCode?: string,
) => ({
  id: variant,
  name: messagesPerVariant[variant].dataLayerName,
  variant: type,
  category: 'bike_insurance',
  brand: country,
  dimension1: 'Main',
  price: formatPrice(prices.getPrice(variant, PaymentRythm.YEARLY)),
  coupon: discountCode ?? undefined,
});

export const pushProductDetailsView = (
  draft: DraftProps,
  prices: PriceInfo,
  variants: Variant[],
  discountCode?: string,
) => {
  // @ts-ignore
  dataLayer.push({
    event: `productDetailView${variants.length}`,
    ecommerce: {
      currencyCode: draft.settings?.currency,
      detail: {
        products: variants.map(variant =>
          createVariantView(
            prices,
            variant,
            draft.risk?.type as Type,
            draft.refs?.country as string,
            discountCode,
          ),
        ),
      },
    },
  });
};

const pushCheckout = (
  draft: Draft,
  prices: PriceInfo,
  step: number,
  discountCode?: string,
) => {
  // @ts-ignore
  dataLayer.push({
    event: `checkout${step}`,
    ecommerce: {
      currencyCode: draft.settings?.currency,
      checkout: {
        actionField: { step },
        products: [
          createVariantView(
            prices,
            draft.terms?.variant as Variant,
            draft.risk?.type as Type,
            draft.refs?.country as string,
            discountCode,
          ),
        ],
      },
    },
  });
};

export const pushPolicyholderCheckout = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 1, discountCode);

export const pushBikeCheckout = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 2, discountCode);

export const pushBikeConditionCheckout = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 3, discountCode);

export const pushBikeSerialNumberCheckout = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 4, discountCode);

export const pushBikeFinalizationCheckout = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 5, discountCode);

export const pushSummaryCheckoutDesktop = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 6, discountCode);

export const pushPaymentCheckoutDesktop = (
  draft: Draft,
  prices: PriceInfo,
  discountCode?: string,
) => pushCheckout(draft, prices, 8, discountCode);

export const pushPurchase = (draft: Draft, contract: any) => {
  // @ts-ignore
  dataLayer.push({
    event: 'ecommerce',
    ecommerce: {
      currencyCode: contract.settings.currency,
      purchase: {
        actionField: {
          id: contract.contractId,
          affiliation: contract?.payment?.paymentMethod
            .substr(15)
            .toLowerCase(),
          revenue: formatPrice(
            contract?.totalCoverage?.priceInfo?.yearlyPremium?.withTaxes,
          ),
          tax: formatPrice(
            contract?.totalCoverage?.priceInfo?.yearlyPremium?.totalTaxes,
          ),
          coupon: draft.discountCodes?.[0],
        },
        products: [
          {
            id: draft.terms?.variant,
            name:
              messagesPerVariant[draft.terms?.variant as Variant].dataLayerName,
            variant: draft.risk?.type,
            category: 'bike_insurance',
            brand: draft.refs?.country,
            dimension1: 'Main',
            price: formatPrice(
              contract?.totalCoverage?.priceInfo?.yearlyPremium?.withTaxes,
            ),
          },
        ],
      },
    },
  });
};

const eventToLabel = (e: any) => (e.target.open ? 'expand' : 'collapse');

export const pushQuoteSections = (eventAction: string, eventLabel: string) => {
  // @ts-ignore
  dataLayer.push({
    event: 'quoteSections',
    eventCategory: 'quote sections',
    eventAction,
    eventLabel,
  });
};

export const pushQuoteSectionsGuarantee = (e: any) =>
  pushQuoteSections('guarantees', eventToLabel(e));

export const pushQuoteSectionsExclusions = (e: any) =>
  pushQuoteSections('exclusions', eventToLabel(e));

export const pushQuoteSectionsEligibility = (e: any) =>
  pushQuoteSections('eligibility rules', eventToLabel(e));

export const pushQuoteSectionsClaim = (e: any) =>
  pushQuoteSections('how to file a claim', eventToLabel(e));

export const pushQuoteSectionsInsurance = (e: any) =>
  pushQuoteSections('insurance documents', eventToLabel(e));

export const pushQuoteSectionsCancel = (e: any) =>
  pushQuoteSections('how to cancel', eventToLabel(e));

export const pushQuoteSectionsInsured = (e: any) =>
  pushQuoteSections('already insured', eventToLabel(e));

export const pushQuoteSectionsContact = (e: any) =>
  pushQuoteSections('contact', eventToLabel(e));

export const createMainProduct = (
  draft: DraftProps,
  prices: PriceInfo,
  variant: Variant,
  actionField: 'landing' | 'update',
) => {
  // @ts-ignore
  dataLayer.push({
    event: 'addToCartMain',
    ecommerce: {
      currencyCode: draft.settings?.currency,
      add: {
        actionField: {
          list: actionField,
        },
        products: [
          {
            id: variant,
            name: messagesPerVariant[variant].dataLayerName,
            variant: draft.risk?.type ?? '',
            category: 'bike_insurance',
            brand: draft.refs?.country,
            dimension1: 'Main',
            price: formatPrice(prices.getPrice(variant, PaymentRythm.YEARLY)),
            coupon: draft.discountCodes?.[0] ?? undefined,
          },
        ],
      },
    },
  });
};

export const pushAddPromocode = (promocode: string, step: string) => {
  // @ts-expect-error
  dataLayer.push({
    event: 'allTagsWithEvents',
    eventCategory: 'promocode',
    eventAction: `entered on ${step}`,
    eventLabel: promocode,
  });
};

export const pushUpsellView = () => {
  // @ts-expect-error
  dataLayer.push({
    event: 'onlyAnalytics',
    eventCategory: 'upsell',
    eventAction: 'summary upsell',
    eventLabel: 'displayed',
  });
};

export const pushUpsellClick = () => {
  // @ts-expect-error
  dataLayer.push({
    event: 'onlyAnalytics',
    eventCategory: 'upsell',
    eventAction: 'summary upsell',
    eventLabel: 'click to upsell',
  });
};
